/**
 * Sidebar - Dashboard navigation with product separation.
 * Two distinct products: KPA Ton Concours (search/info) and Boss Ton Concours (quiz/prep).
 */
import {
  BarChart3,
  Bell,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Coins,
  CreditCard,
  Database,
  FileQuestion,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Map,
  Search,
  Settings,
  Sparkles,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import React from 'react';

export type DashboardSection =
  // ── Global ──
  | 'overview'
  | 'settings'
  // ── KPA Ton Concours ──
  | 'kpa-overview'
  | 'kpa-concours'
  | 'kpa-users'
  | 'kpa-credits'
  | 'kpa-notifications'
  | 'kpa-referral'
  | 'kpa-analytics'
  // ── Boss Ton Concours ──
  | 'boss-overview'
  | 'learning'
  | 'lesson-manager'
  | 'payments'
  | 'questions'
  | 'database'
  | 'boss-users'
  | 'boss-analytics';

interface SidebarProps {
  currentSection: DashboardSection;
  onSectionChange: (section: DashboardSection) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onLogout: () => void;
}

type MenuItem = {
  id: DashboardSection;
  label: string;
  icon: React.ReactNode;
  badge?: string;
};

type MenuGroup = {
  label: string;
  color: string;
  dot: string;
  items: MenuItem[];
};

const menuGroups: MenuGroup[] = [
  {
    label: 'Global',
    color: 'text-gray-400',
    dot: 'bg-gray-500',
    items: [
      { id: 'overview', label: 'Vue d\'ensemble', icon: <LayoutDashboard className="w-4 h-4" /> },
    ],
  },
  {
    label: 'KPA Ton Concours',
    color: 'text-orange-400',
    dot: 'bg-orange-500',
    items: [
      { id: 'kpa-overview', label: 'Tableau de bord', icon: <Trophy className="w-4 h-4" /> },
      { id: 'kpa-concours', label: 'Concours', icon: <Search className="w-4 h-4" />, badge: 'PROD' },
      { id: 'kpa-users', label: 'Utilisateurs', icon: <Users className="w-4 h-4" /> },
      { id: 'kpa-credits', label: 'Crédits & Achats', icon: <Coins className="w-4 h-4" /> },
      { id: 'kpa-notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
      { id: 'kpa-referral', label: 'Parrainage', icon: <Zap className="w-4 h-4" /> },
      { id: 'kpa-analytics', label: 'Statistiques', icon: <BarChart3 className="w-4 h-4" /> },
    ],
  },
  {
    label: 'Boss Ton Concours',
    color: 'text-blue-400',
    dot: 'bg-blue-500',
    items: [
      { id: 'boss-overview', label: 'Tableau de bord', icon: <Map className="w-4 h-4" /> },
      { id: 'learning', label: 'Apprentissage', icon: <GraduationCap className="w-4 h-4" /> },
      { id: 'lesson-manager', label: 'Gestion Leçons', icon: <BookOpen className="w-4 h-4" /> },
      { id: 'payments', label: 'Abonnements', icon: <CreditCard className="w-4 h-4" /> },
      { id: 'questions', label: 'Générateur Questions', icon: <FileQuestion className="w-4 h-4" /> },
      { id: 'database', label: 'Base de données', icon: <Database className="w-4 h-4" /> },
      { id: 'boss-users', label: 'Utilisateurs', icon: <Users className="w-4 h-4" /> },
      { id: 'boss-analytics', label: 'Statistiques', icon: <BarChart3 className="w-4 h-4" /> },
    ],
  },
  {
    label: 'Configuration',
    color: 'text-gray-400',
    dot: 'bg-gray-500',
    items: [
      { id: 'settings', label: 'Paramètres', icon: <Settings className="w-4 h-4" /> },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  currentSection,
  onSectionChange,
  isCollapsed,
  onToggleCollapse,
  onLogout,
}) => {
  return (
    <aside
      className={`bg-gray-900 text-white flex flex-col transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'
        }`}
    >
      {/* Logo */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          {!isCollapsed && (
            <div>
              <h1 className="font-bold text-base leading-tight">Admin Panel</h1>
              <p className="text-xs text-gray-400">Gestion des produits</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {menuGroups.map((group) => (
          <div key={group.label} className="mb-2">
            {/* Group header */}
            {!isCollapsed && (
              <div className="flex items-center gap-2 px-4 py-1.5">
                <span className={`w-2 h-2 rounded-full ${group.dot}`} />
                <span className={`text-xs font-semibold uppercase tracking-wider ${group.color}`}>
                  {group.label}
                </span>
              </div>
            )}
            {isCollapsed && group.label !== 'Global' && group.label !== 'Configuration' && (
              <div className="flex justify-center py-1">
                <span className={`w-2 h-2 rounded-full ${group.dot}`} />
              </div>
            )}
            <ul className="space-y-0.5 px-2">
              {group.items.map((item) => {
                const isActive = currentSection === item.id;
                const activeClass = group.dot.includes('orange')
                  ? 'bg-orange-600 text-white'
                  : group.dot.includes('blue')
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-600 text-white';
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => onSectionChange(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${isActive
                        ? activeClass
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        }`}
                      title={isCollapsed ? item.label : undefined}
                    >
                      {item.icon}
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          {item.badge && (
                            <span className={`px-1.5 py-0.5 text-xs rounded-full font-bold ${item.badge === 'PROD' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-black'}`}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom actions */}
      <div className="p-4 border-t border-gray-800 space-y-2">
        {/* Logout */}
        <button
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-red-400 hover:text-white hover:bg-red-600 rounded-lg transition"
          title={isCollapsed ? 'Déconnexion' : undefined}
        >
          <LogOut className="w-5 h-5" />
          {!isCollapsed && <span>Déconnexion</span>}
        </button>

        {/* Collapse button */}
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition"
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span>Réduire</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
